if (!customElements.get('almila-promo-hero')) {
  // Space between a view and its neighbour while swiping, the same as the gap between the cards.
  const SWIPE_GAP = 12;
  // The shop's time zone (Settings › General), in which the "Промоция" dates and Liquid's 'now' are days.
  const TIME_ZONE = 'Europe/Sofia';
  const DAY = 86400000;
  const dayParts = date => Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, year: 'numeric', month: 'numeric', day: 'numeric' })
    .formatToParts(date).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
  const isoDay = ({ year, month, day }) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const utcDay = iso => Date.UTC(...iso.split('-').map((part, index) => Number(part) - (index === 1 ? 1 : 0)));
  customElements.define('almila-promo-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.applyDates();
      this.views = Array.from(this.querySelectorAll('[data-view]:not([data-off])'));
      this.index = Math.max(0, this.views.findIndex(view => !view.hidden));
      this.paused = false;
      this.editorPaused = this.dataset.editor === 'true';
      this.visible = !('IntersectionObserver' in window);
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Phones have no pause button, so the views change only when the visitor swipes or taps a dot (WCAG 2.2.2).
      this.phone = window.matchMedia('(max-width: 768.98px)');
      this.interval = Math.max(5000, Number(this.dataset.interval || 15) * 1000);
      this.visual = this.querySelector('.almila-hero__visual');
      this.controls = this.querySelector('[data-controls]');
      this.pauseButton = this.querySelector('[data-pause]');
      this.dotGroup = this.querySelector('[data-dots]');
      this.bars = Array.from(this.querySelectorAll('[data-progress]'));
      this.style.setProperty('--ah-interval', `${this.interval}ms`);
      this.show(this.index);
      if (this.views.length < 2) return;
      this.controls.hidden = false;
      this.setPaused(false);
      this.fitDots();
      this.querySelector('[data-previous]').addEventListener('click', () => this.move(-1), { signal });
      this.querySelector('[data-next]').addEventListener('click', () => this.move(1), { signal });
      this.pauseButton.addEventListener('click', () => {
        this.setPaused(!this.paused);
        this.schedule();
      }, { signal });
      this.bars.forEach((bar, position) => {
        bar.addEventListener('click', () => this.go(position), { signal });
        bar.addEventListener('focus', () => this.warm(), { signal });
        bar.addEventListener('keydown', event => {
          let next = position;
          if (event.key === 'ArrowRight') next += 1;
          else if (event.key === 'ArrowLeft') next -= 1;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.bars.length - 1;
          else return;
          event.preventDefault();
          next = Math.max(0, Math.min(this.bars.length - 1, next));
          this.bars[next].focus();
          this.go(next);
        }, { signal });
      });
      this.visual.addEventListener('pointerdown', event => this.startSwipe(event), { signal });
      this.visual.addEventListener('pointermove', event => this.dragSwipe(event), { signal });
      this.visual.addEventListener('pointerup', event => this.endSwipe(event, false), { signal });
      this.visual.addEventListener('pointercancel', event => this.endSwipe(event, true), { signal });
      // A swipe that ends on a card must not open it.
      this.visual.addEventListener('click', event => {
        if (performance.now() - (this.swipedAt || -Infinity) > 500) return;
        event.preventDefault();
        event.stopPropagation();
      }, { signal, capture: true });
      this.addEventListener('focusin', () => this.schedule(), { signal });
      this.addEventListener('focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => this.schedule(), 0);
      }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => this.schedule(), { signal });
      this.phone.addEventListener('change', () => {
        this.fitDots();
        this.schedule();
      }, { signal });
      for (const eventName of ['shopify:block:select', 'shopify:section:select']) {
        document.addEventListener(eventName, event => {
          if (!this.contains(event.target) && !event.target.contains(this)) return;
          this.editorPaused = true;
          const selected = this.views.findIndex(view => view === event.target || view.contains(event.target) || view.dataset.blockId === event.detail?.blockId);
          if (selected >= 0) this.show(selected);
          this.schedule();
        }, { signal });
      }
      for (const eventName of ['shopify:block:deselect', 'shopify:section:deselect']) {
        document.addEventListener(eventName, event => {
          if (!this.contains(event.target) && !event.target.contains(this)) return;
          this.editorPaused = this.dataset.editor === 'true';
          this.schedule();
        }, { signal });
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: 0.1 });
        this.observer.observe(this);
      }
      this.schedule();
    }
    // Nikolay's 18 Oct item 4 (sections/almila-promo-hero.liquid): the page chose the views, the button and the date
    // line on the day it was made; a cached page keeps that day, so the visitor's date in the shop's time zone
    // chooses again. The running promotion is the first entry whose dates include today; its own views show, else
    // the default views (no promotion chosen). The date line counts down to the last day.
    applyDates() {
      if (this.dataset.editor === 'true') return;
      let promotions;
      try { promotions = JSON.parse(this.dataset.promotions || '[]'); } catch { return; }
      const today = isoDay(dayParts(new Date()));
      const running = promotions.find(promotion => promotion.starts <= today && today <= promotion.ends);
      const views = Array.from(this.querySelectorAll('[data-view]'));
      const shown = running && views.some(view => view.dataset.promotion === running.handle) ? running.handle : '';
      const changed = views.some(view => (view.dataset.promotion !== shown) !== view.hasAttribute('data-off'));
      if (changed) {
        views.forEach(view => view.toggleAttribute('data-off', view.dataset.promotion !== shown));
        const on = views.filter(view => !view.hasAttribute('data-off'));
        views.forEach(view => {
          const first = view === on[0];
          view.hidden = !first;
          view.inert = !first;
          view.setAttribute('aria-hidden', String(!first));
        });
        this.classList.toggle('almila-hero--no-visual', on.length === 0);
        const dots = this.querySelector('[data-dots]');
        if (dots) {
          this.querySelector('[data-controls]').classList.toggle('almila-hero__controls--off', on.length < 2);
          dots.replaceChildren(...(on.length < 2 ? [] : on.map((view, position) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.dataset.progress = '';
            dot.setAttribute('aria-label', dots.dataset.label.replace('__N__', position + 1).replace('__C__', on.length));
            dot.setAttribute('aria-current', String(position === 0));
            return dot;
          })));
        }
      }
      this.querySelectorAll('[data-when]').forEach(button => { button.hidden = (button.dataset.when === 'promotion') !== !!running; });
      const line = this.querySelector('[data-validity]');
      if (!line) return;
      line.hidden = !running;
      if (!running) return;
      const left = Math.round((utcDay(running.ends) - utcDay(today)) / DAY) + 1;
      const [, month, day] = running.ends.split('-').map(Number);
      line.textContent = left <= 1 ? line.dataset.lastDay
        : left <= 7 ? line.dataset.daysLeft.replace('__N__', left)
        : line.dataset.until.replace('__D__', `${day} ${line.dataset.months.split(',')[month - 1]}`);
    }
    show(index) {
      const restoreFocus = this.views.some(view => view.contains(document.activeElement));
      this.index = index;
      this.views.forEach((view, position) => {
        const active = position === index;
        view.hidden = !active;
        view.inert = !active;
        view.setAttribute('aria-hidden', String(!active));
      });
      this.bars.forEach((bar, position) => {
        bar.classList.toggle('is-active', position === index);
        bar.setAttribute('aria-current', String(position === index));
      });
      if (restoreFocus) this.views[index]?.querySelector('a[href]')?.focus();
    }
    move(direction) {
      this.show((this.index + direction + this.views.length) % this.views.length);
      this.schedule();
    }
    // A dot: the chosen view slides in from its side.
    go(index) {
      if (index === this.index || this.settling) return;
      const direction = index > this.index ? 1 : -1;
      this.show(index);
      this.schedule();
      if (this.motion.matches) return;
      this.views[index].animate([
        { transform: `translateX(${direction * 32}px)`, opacity: 0 },
        { transform: 'none', opacity: 1 }
      ], { duration: 240, easing: 'cubic-bezier(.2, .8, .2, 1)' });
    }
    // Desktop keeps the timer bars as decoration; on phones they are the dots.
    fitDots() {
      this.dotGroup.inert = !this.phone.matches;
    }
    // The other views' cards load lazily; fetch them as soon as the visitor reaches for the banner.
    warm() {
      if (this.warmed) return;
      this.warmed = true;
      this.views.forEach(view => view.querySelectorAll('img[loading="lazy"]').forEach(image => { image.loading = 'eager'; }));
    }
    startSwipe(event) {
      if (event.pointerType === 'mouse' || !event.isPrimary || this.drag || this.settling) return;
      this.warm();
      if (this.controls.contains(event.target)) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, start: event.timeStamp, dx: 0, axis: '', peek: -1 };
    }
    dragSwipe(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.axis) {
        const dy = event.clientY - drag.y;
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        // A mostly vertical move is a page scroll: let the browser have it.
        if (Math.abs(dy) >= Math.abs(dx)) {
          this.drag = null;
          return;
        }
        drag.axis = 'x';
        drag.width = this.visual.clientWidth;
        try { this.visual.setPointerCapture(drag.id); } catch { /* the pointer is already gone */ }
        clearTimeout(this.timer);
        this.visual.classList.add('is-swiping');
      }
      drag.dx = dx;
      // Dragging left brings the next view in from the right; dragging right, the previous one from the left.
      const direction = dx < 0 ? 1 : -1;
      const peek = (this.index + direction + this.views.length) % this.views.length;
      if (drag.peek !== peek) {
        this.unpeek(drag.peek);
        const view = this.views[peek];
        view.hidden = false;
        view.classList.add('is-peek');
        drag.peek = peek;
      }
      drag.direction = direction;
      this.views[this.index].style.transform = `translate3d(${dx}px, 0, 0)`;
      this.views[peek].style.transform = this.offset(direction, dx);
    }
    endSwipe(event, cancelled) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.drag = null;
      if (!drag.axis) return;
      this.swipedAt = performance.now();
      const distance = Math.abs(drag.dx);
      const speed = distance / Math.max(1, event.timeStamp - drag.start);
      // A fifth of the width, or a quick flick, turns the view.
      const commit = !cancelled && (distance > drag.width * 0.2 || (distance > 24 && speed > 0.35));
      this.settle(drag, commit);
    }
    settle(drag, commit) {
      const { direction, peek } = drag;
      const current = this.views[this.index];
      const target = commit ? peek : this.index;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(this.settleTimer);
        this.views.forEach(view => {
          view.style.transform = '';
          view.classList.remove('is-peek');
        });
        this.visual.classList.remove('is-swiping', 'is-settling');
        this.settling = false;
        this.show(target);
        this.schedule();
      };
      if (this.motion.matches) return finish();
      this.settling = true;
      this.visual.classList.add('is-settling');
      const shift = commit ? -direction : 0;
      current.style.transform = this.offset(shift, 0);
      this.views[peek].style.transform = this.offset(direction + shift, 0);
      current.addEventListener('transitionend', finish, { once: true });
      this.settleTimer = setTimeout(finish, 400);
    }
    // A view's place in whole widths (plus the gap) from the banner's position, moved by the finger's distance.
    offset(widths, dx) {
      return `translate3d(calc(${widths * 100}% + ${widths * SWIPE_GAP + dx}px), 0, 0)`;
    }
    unpeek(index) {
      const view = this.views[index];
      if (!view) return;
      view.hidden = true;
      view.style.transform = '';
      view.classList.remove('is-peek');
    }
    setPaused(paused) {
      this.paused = paused;
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      const pauseLabel = this.querySelector('[data-pause-label]');
      const playLabel = this.querySelector('[data-play-label]');
      pauseLabel.hidden = paused;
      playLabel.hidden = !paused;
      this.pauseButton.setAttribute('aria-label', paused ? playLabel.textContent : pauseLabel.textContent);
    }
    schedule() {
      clearTimeout(this.timer);
      const running = !(!this.abort || this.views.length < 2 || this.paused || this.editorPaused || !this.visible || this.motion.matches || this.phone.matches || this.drag?.axis || this.settling || document.hidden || this.contains(document.activeElement));
      // The active bar fills over the interval while the views rotate; each restart of the timer restarts it.
      this.bars.forEach(bar => bar.classList.remove('is-running'));
      const bar = this.bars[this.index];
      if (running && bar) {
        void bar.offsetWidth;
        bar.classList.add('is-running');
      }
      if (!running) return;
      this.timer = setTimeout(() => {
        this.show((this.index + 1) % this.views.length);
        this.schedule();
      }, this.interval);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      clearTimeout(this.timer);
      clearTimeout(this.focusTimer);
      clearTimeout(this.settleTimer);
      this.observer?.disconnect();
      this.observer = null;
    }
  });
}
